import { useState } from "react";
import type { BrianConversationTurn } from "../api/userReports";
import { getApiErrorMessage } from "../utils";

interface BrianResponse {
  type: string;
  message: string;
}

interface UseBrianProposalOptions<TResponse extends BrianResponse> {
  request: (
    message: string,
    conversation: BrianConversationTurn[],
  ) => Promise<TResponse>;
  errorMessage?: string;
}

export function useBrianProposal<TResponse extends BrianResponse>({
  request,
  errorMessage = "Brian could not complete that request",
}: UseBrianProposalOptions<TResponse>) {
  const [conversation, setConversation] = useState<BrianConversationTurn[]>([]);
  const [response, setResponse] = useState<TResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function send(message: string) {
    const trimmed = message.trim();
    if (!trimmed || loading) return;

    setLoading(true);
    setResponse(null);
    setError("");
    try {
      const result = await request(trimmed, conversation);
      setResponse(result);
      setConversation((current) => [
        ...current,
        { role: "user", content: trimmed },
        { role: "assistant", content: result.message },
      ].slice(-10) as BrianConversationTurn[]);
      return result;
    } catch (caught) {
      setError(getApiErrorMessage(caught, errorMessage));
    } finally {
      setLoading(false);
    }
  }

  function dismiss() {
    setResponse(null);
    setError("");
  }

  function reset() {
    setConversation([]);
    setResponse(null);
    setError("");
    setLoading(false);
  }

  return { conversation, response, loading, error, send, dismiss, reset };
}
